/* Converto — product.js
   Page produit : variantes, galerie, paliers de quantité, bundle, sticky ATC. */

/* ---------- Sélecteur de variantes ---------- */
class VariantSelects extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.productData = this.getProductData();
    this.addEventListener('change', () => this.onChange(), { signal: this._lifecycle.signal });
  }
  getProductData() {
    const script = (this.closest('[data-product-scope]') || this.closest('.shopify-section') || document).querySelector(`[data-product-json="${this.dataset.productId}"]`);
    return script ? JSON.parse(script.textContent) : null; // tableau de variantes (état allégé)
  }
  selectedOptions() {
    return Array.from(this.querySelectorAll('fieldset')).map(
      (fs) => fs.querySelector('input:checked')?.value
    );
  }
  onChange() {
    if (!this.productData) return;
    const options = this.selectedOptions();
    const variant = this.productData.find((v) =>
      v.options.every((opt, i) => opt === options[i])
    );
    this.updateLabels();
    document.dispatchEvent(new CustomEvent('variant:changed', {
      detail: { variant, productId: this.dataset.productId }
    }));
    if (!variant) return;
    // Met à jour l'URL sans recharger
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
  updateLabels() {
    this.querySelectorAll('fieldset').forEach((fs) => {
      const value = fs.querySelector('input:checked')?.value;
      const label = fs.querySelector('[data-selected-value]');
      if (label && value) label.textContent = value;
    });
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
}
customElements.define('variant-selects', VariantSelects);

/* ---------- Moteur d'état de variante ----------
   Source de vérité unique : à chaque changement de variante, TOUT est
   actualisé (prix, unité, économie, badge, stock, paliers, abonnements,
   bundle, bouton, sticky ATC, média). Données : JSON [data-product-json]. */
// Estimation facultative d'une remise configurée dans l'éditeur, hors totaux d'achat.
Converto.updateDiscountEstimate = function (root, total) {
  root.querySelectorAll('[data-discount-estimate]').forEach((estimate) => {
    const percent = Math.min(100, Math.max(0, Number(estimate.dataset.percent) || 0));
    const calculated = Math.round(total * (100 - percent) / 100);
    const values = { total: calculated, saving: total - calculated, unit: Math.round(calculated / (Number(estimate.dataset.quantity) || 1)) };
    Object.entries(values).forEach(([key, value]) => {
      const target = estimate.querySelector(`[data-estimate-${key}]`);
      if (target) target.textContent = Converto.formatMoney(value);
    });
  });
};

Converto.updateProductState = function (root, variant) {
  const s = window.themeSettings.strings;
  const fm = Converto.formatMoney;
  const btn = root.querySelector('[data-add-to-cart]');
  const btnLabel = btn?.querySelector('.btn__label');
  // Le sticky vit dans la même section que le scope, jamais dans un autre produit
  const sticky = root.querySelector('sticky-atc')
    || (root.closest('.shopify-section') || document).querySelector('sticky-atc');
  const payButtons = root.querySelectorAll('.shopify-payment-button button');

  if (!variant) {
    // Combinaison d'options sans variante : TOUTES les voies d'achat sont neutralisées
    if (btn) {
      btn.setAttribute('disabled', '');
      if (btnLabel) btnLabel.textContent = s.unavailable;
    }
    payButtons.forEach((b) => { b.disabled = true; });
    if (sticky) sticky.querySelector('[data-sticky-add]')?.setAttribute('disabled', '');
    const stockEl = root.querySelector('[data-stock-indicator]');
    if (stockEl) stockEl.hidden = true;
    const savingsEl = root.querySelector('[data-savings]');
    if (savingsEl) savingsEl.hidden = true;
    return;
  }

  payButtons.forEach((b) => { b.disabled = !variant.available; });

  // Sens sticky → fiche : les options classiques suivent la variante (sans redispatch)
  const vs = root.querySelector('variant-selects');
  if (vs && variant.options) {
    vs.querySelectorAll('fieldset').forEach((fs, i) => {
      const input = Array.from(fs.querySelectorAll('input')).find((inp) => inp.value === variant.options[i]);
      if (input && !input.checked) input.checked = true;
    });
    if (typeof vs.updateLabels === 'function') vs.updateLabels();
  }

  // Formulaire : id de variante (inputs cachés, pas les selects gérés à part)
  root.querySelectorAll('form input[name="id"]').forEach((el) => { el.value = variant.id; });

  // Bouton d'achat : état + libellé (personnalisé via data-label-add, et
  // libellé précommande quand la vente continue après épuisement du stock)
  if (btn) {
    btn.toggleAttribute('disabled', !variant.available);
    if (btnLabel) {
      const backorder = variant.available && variant.inv_managed && variant.inv_qty <= 0;
      btnLabel.textContent = variant.available
        ? ((backorder && btnLabel.dataset.labelPreorder) || btnLabel.dataset.labelAdd || s.addToCart)
        : s.soldOut;
    }
  }

  // Prix principal + prix barré + prix unitaire (mémorisé pour le retour
  // depuis un abonnement : applyPlanPricing le restaure tel quel)
  const priceEl = root.querySelector('[data-price]');
  if (priceEl) {
    Converto.setPrice(priceEl, variant);
    priceEl.dataset.standardHtml = priceEl.innerHTML;
    priceEl.dataset.comparePrice = variant.compare_at_price || 0;
    priceEl.dataset.unitPrice = variant.unit_price || 0;
    priceEl.dataset.unitRef = variant.unit_ref || '';
  }

  // Ligne d'économie
  const savings = root.querySelector('[data-savings]');
  if (savings) {
    const diff = variant.compare_at_price - variant.price;
    if (diff > 0 && savings.dataset.savingsTemplate) {
      savings.textContent = savings.dataset.savingsTemplate.replace('{amount}', fm(diff));
      savings.hidden = false;
    } else {
      savings.hidden = true;
    }
  }

  // Badge promotion
  const saleBadge = root.querySelector('[data-sale-badge]');
  if (saleBadge) saleBadge.hidden = !(variant.compare_at_price > variant.price);

  // Indicateur de stock : mêmes quatre états que le rendu Liquid
  // (rupture / réapprovisionnement vendable / stock faible / stock normal)
  const stock = root.querySelector('[data-stock-indicator]');
  if (stock) {
    const threshold = parseInt(stock.dataset.threshold, 10) || 10;
    let state = '';
    if (!variant.available) state = 'out';
    else if (variant.inv_managed) {
      if (variant.inv_qty <= 0) state = 'backorder';
      else state = variant.inv_qty <= threshold ? 'low' : 'in';
    }
    stock.hidden = state === '';
    ['low', 'out', 'backorder'].forEach((m) => {
      stock.classList.toggle('stock-indicator--' + m, state === m);
    });
    const text = stock.querySelector('[data-stock-text]');
    if (text && state !== '') {
      if (state === 'low') text.textContent = (stock.dataset.textLow || '').replace('{count}', variant.inv_qty);
      else text.textContent = stock.dataset['text' + state.charAt(0).toUpperCase() + state.slice(1)] || '';
    }
    const track = stock.querySelector('.stock-indicator__track');
    if (track) {
      track.hidden = !(state === 'low' && stock.dataset.showBar === 'true');
      const fill = stock.querySelector('[data-stock-fill]');
      if (fill) fill.style.width = Math.min(100, Math.max(8, Math.round((variant.inv_qty * 100) / threshold))) + '%';
    }
  }

  // P04 : le prix catalogue alimente les totaux ; l'estimation reste séparée.
  root.querySelectorAll('[data-tier]').forEach((tier) => {
    const qty = parseInt(tier.dataset.qty, 10) || 1;
    const total = variant.price * qty;
    const input = tier.querySelector('input[name="qb-offer"]');
    if (input) input.dataset.total = total;
    const finalEl = tier.querySelector('[data-tier-final]');
    if (finalEl) finalEl.textContent = fm(total);
    Converto.updateDiscountEstimate(tier, total);
  });

  // Abonnements : offre intégrée au sélecteur + plans du formulaire
  const allocations = {};
  (variant.allocations || []).forEach((a) => { allocations[a.plan] = a.price; });
  const subOffer = root.querySelector('input[name="qb-offer"][data-selling-plan]');
  if (subOffer) {
    const row = subOffer.closest('.quantity-break');
    const subPrice = allocations[parseInt(subOffer.dataset.sellingPlan, 10)];
    if (row) {
      if (subPrice == null) {
        row.hidden = true;
        // L'abonnement était sélectionné mais n'existe pas pour cette variante :
        // retour propre au palier 1 (resynchronise quantité, selling plan et prix)
        if (subOffer.checked) {
          const firstTier = root.querySelector('[data-tier] input[name="qb-offer"]');
          if (firstTier) {
            firstTier.checked = true;
            firstTier.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } else {
        row.hidden = false;
        subOffer.dataset.total = subPrice;
        const c = row.querySelector('[data-sub-compare]');
        if (c) { c.textContent = fm(variant.price); c.hidden = !(subPrice < variant.price); }
        const f = row.querySelector('[data-sub-final]');
        if (f) f.textContent = fm(subPrice);
      }
    }
  }
  root.querySelectorAll('[data-plan-price]').forEach((el) => {
    if (el.dataset.planId === '') { el.textContent = fm(variant.price); return; }
    const p = allocations[parseInt(el.dataset.planId, 10)];
    el.hidden = p == null;
    if (p != null) el.textContent = fm(p);
  });
  // Éligibilité des plans pour cette variante : allocation mémorisée sur la
  // radio, plan indisponible désactivé, repli sur l'achat unique si besoin
  root.querySelectorAll('input[name="selling_plan"]').forEach((r) => {
    if (r.value === '') return;
    const p = allocations[parseInt(r.value, 10)];
    r.dataset.allocPrice = p != null ? p : '';
    r.disabled = p == null;
    r.closest('.selling-plan')?.classList.toggle('is-unavailable', p == null);
    if (p == null && r.checked) {
      const fallback = root.querySelector('input[name="selling_plan"][value=""]')
        || root.querySelector('input[name="selling_plan"]:not([disabled])');
      if (fallback && fallback !== r) {
        fallback.checked = true;
        fallback.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  // Bundle : variante et prix du produit courant + totaux recalculés
  const bundle = root.querySelector('bundle-form');
  if (bundle) {
    const currentItem = bundle.querySelector('[data-bundle-current]');
    if (currentItem) {
      currentItem.dataset.bundleVariant = variant.id;
      currentItem.dataset.itemPrice = variant.price;
      currentItem.dataset.nativeMin = variant.quantity?.rule?.min || 1;
      currentItem.dataset.nativeStep = variant.quantity?.rule?.increment || 1;
      currentItem.dataset.nativeMax = variant.quantity?.rule?.max ?? '';
      bundle.syncRules?.();
      const p = currentItem.querySelector('[data-bundle-current-price]');
      if (p) p.textContent = fm(variant.price);
    }
    let total = 0;
    bundle.querySelectorAll('[data-item-price]').forEach((item) => {
      total += parseInt(item.dataset.itemPrice, 10) || 0;
    });
    const f = bundle.querySelector('[data-bundle-final]');
    if (f) f.textContent = fm(total);
    Converto.updateDiscountEstimate(bundle, total);
  }

  // Prix dans le bouton d'achat (tient compte du palier/quantité sélectionnés)
  root.dataset.unitPrice = variant.price;
  root.querySelectorAll('input[name="offer-variant"]').forEach((r) => {
    r.checked = String(variant.id) === r.value;
  });
  const btnPrice = root.querySelector('[data-btn-price]');
  if (btnPrice) btnPrice.dataset.unitPrice = variant.price;
  // Prix effectif selon l'abonnement sélectionné, puis totaux (bouton,
  // paiement fractionné, sticky) sur ce prix
  Converto.applyPlanPricing(root);
  Converto.quantity.sync(root, variant);
  Converto.updateTotals(root);

  // Sticky add-to-cart : sélection et disponibilité (le prix est géré par
  // updateTotals ci-dessus, sur le total courant : ne pas l'écraser ici)
  if (sticky) {
    sticky.dataset.variantId = variant.id;
    const select = sticky.querySelector('select[name="id"]');
    if (select) select.value = variant.id;
    const sBtn = sticky.querySelector('[data-sticky-add]');
    if (sBtn) sBtn.toggleAttribute('disabled', !variant.available);
  }

  // Galerie : médias filtrés par variante puis image de la variante
  const gallery = root.querySelector('product-gallery');
  if (gallery && gallery.filterForVariant) {
    gallery.filterForVariant(variant.id);
    if (variant.media_id) gallery.showMedia(variant.media_id);
  } else if (variant.media_id) {
    const media = root.querySelector(`[data-media-id="${variant.media_id}"]`);
    media?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }
};

document.addEventListener('variant:changed', (e) => {
  const { variant, productId } = e.detail;
  document.querySelectorAll(`[data-product-scope="${productId}"]`).forEach((root) => {
    Converto.updateProductState(root, variant);
  });
});

/* ---------- Galerie : vignettes, variante présélectionnée, regroupement ---------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.track = this.querySelector('.slider');
    this.thumbs = this.querySelectorAll('.product-gallery__thumb');
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const slide = this.track?.children[i];
        slide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }, { signal: this._lifecycle.signal });
    });
    this.track?.addEventListener('scroll', () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.syncActive(), 80);
    }, { signal: this._lifecycle.signal, passive: true });
    this.initRail();
    this.initVariantMedia();
    this.syncActive();
  }
  /* Rail de miniatures : les flèches déplacent seulement le rail, jamais le
     média affiché. Commandes et fondu disparaissent si tout tient à l'écran. */
  initRail() {
    this.rail = this.querySelector('[data-gallery-rail]');
    this.list = this.querySelector('.product-gallery__thumbs');
    this.counter = this.querySelector('[data-gallery-count]');
    if (!this.rail || !this.list) return;
    const { signal } = this._lifecycle;
    this.railPrev = this.rail.querySelector('[data-rail-prev]');
    this.railNext = this.rail.querySelector('[data-rail-next]');
    this.railFade = this.rail.querySelector('.product-gallery__fade');
    this.railPrev?.addEventListener('click', () => this.scrollRail(-1), { signal });
    this.railNext?.addEventListener('click', () => this.scrollRail(1), { signal });
    this.list.addEventListener('scroll', () => {
      clearTimeout(this._rt);
      this._rt = setTimeout(() => this.syncRail(), 60);
    }, { signal, passive: true });
    this.main = this.querySelector('.product-gallery__main');
    this._railObserver = new ResizeObserver(() => this.syncRail());
    this._railObserver.observe(this.list);
    if (this.main) this._railObserver.observe(this.main);
    this.syncRail();
  }
  railVertical() { return getComputedStyle(this.list).flexDirection.startsWith('column'); }
  railMetrics() {
    const vertical = this.railVertical();
    const position = vertical ? this.list.scrollTop : this.list.scrollLeft;
    const size = vertical ? this.list.clientHeight : this.list.clientWidth;
    const total = vertical ? this.list.scrollHeight : this.list.scrollWidth;
    return { vertical, position, size, total };
  }
  scrollRail(direction) {
    const { vertical, size } = this.railMetrics();
    // Un défilement de 80 % de la fenêtre garde une miniature en repère.
    const step = direction * Math.max(120, size * 0.8);
    this.list.scrollBy(vertical ? { top: step } : { left: step });
  }
  syncRail() {
    if (!this.rail || !this.list) return;
    // En colonne, le rail s'aligne sur la hauteur du média affiché.
    if (this.main && this.railVertical()) this.rail.style.setProperty('--pg-rail-height', `${this.main.clientHeight}px`);
    else this.rail.style.removeProperty('--pg-rail-height');
    const { position, size, total } = this.railMetrics();
    const overflows = total - size > 2;
    const atStart = position < 2;
    const atEnd = position >= total - size - 2;
    if (this.railPrev) { this.railPrev.hidden = !overflows; this.railPrev.disabled = atStart; }
    if (this.railNext) { this.railNext.hidden = !overflows; this.railNext.disabled = atEnd; }
    if (this.railFade) this.railFade.hidden = !overflows || atEnd;
  }
  /* Amène la miniature active dans la fenêtre sans toucher au défilement de
     la page (scrollIntoView entraînerait aussi les ancêtres). */
  revealThumb(thumb) {
    if (!this.list || !thumb || thumb.hidden) return;
    const { vertical, position, size } = this.railMetrics();
    const start = vertical ? thumb.offsetTop - this.list.offsetTop : thumb.offsetLeft - this.list.offsetLeft;
    const span = vertical ? thumb.offsetHeight : thumb.offsetWidth;
    if (start < position) this.list.scrollTo(vertical ? { top: start } : { left: start });
    else if (start + span > position + size) {
      const target = start + span - size;
      this.list.scrollTo(vertical ? { top: target } : { left: target });
    }
  }
  updateCounter(index) {
    if (!this.counter || !this.track) return;
    const visible = Array.from(this.track.children).filter((slide) => !slide.hidden);
    const position = visible.indexOf(this.track.children[index]) + 1;
    if (!position || visible.length < 2) { this.counter.hidden = true; return; }
    this.counter.hidden = false;
    const label = this.counter.dataset.countLabel || '{index} / {count}';
    this.counter.querySelector('[data-count-text]').textContent = `${position} / ${visible.length}`;
    this.counter.querySelector('[data-count-sr]').textContent = label.replace('{index}', position).replace('{count}', visible.length);
  }
  /* La variante réellement présélectionnée (cartes d'offre) peut différer du
     premier média : filtrage et position appliqués dès le chargement */
  initVariantMedia() {
    const root = this.closest('[data-product-scope]');
    const idInput = root?.querySelector('form[action*="/cart/add"] [name="id"]');
    const json = root?.querySelector('[data-product-json]');
    if (!idInput || !json) return;
    try {
      const variant = JSON.parse(json.textContent).find((v) => String(v.id) === String(idInput.value));
      if (!variant) return;
      this.filterForVariant(variant.id);
      this.showMedia(variant.media_id, false);
    } catch (e) { /* galerie inchangée */ }
  }
  /* Regroupement par variante (data-variant-media posé en Liquid) :
     un média sans rattachement reste visible pour toutes les variantes */
  filterForVariant(variantId) {
    if (!this.track) return;
    Array.from(this.track.children).forEach((slide, i) => {
      const ids = (slide.dataset.variantMedia || '').split(',').filter(Boolean);
      const hide = ids.length > 0 && !ids.includes(String(variantId));
      slide.hidden = hide;
      if (this.thumbs[i]) this.thumbs[i].hidden = hide;
    });
    this.syncRail();
  }
  showMedia(mediaId, smooth = true) {
    if (!this.track || !mediaId) return;
    const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide || slide.hidden) return;
    if (smooth) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    // 'instant' court-circuite le scroll-behavior: smooth du rail (position
    // initiale sans animation au chargement)
    else this.track.scrollTo({ left: slide.offsetLeft - this.slideBase(), behavior: 'instant' });
    this.syncActive();
  }
  slideBase() {
    const first = Array.from(this.track.children).find((s) => !s.hidden);
    return first ? first.offsetLeft : 0;
  }
  syncActive() {
    if (!this.track || !this.thumbs.length) return;
    // Repérage par position réelle des slides : reste juste quand le mode
    // « aperçu suivant » réduit leur largeur sous celle du rail
    const base = this.slideBase();
    let index = 0;
    let best = Infinity;
    Array.from(this.track.children).forEach((slide, i) => {
      if (slide.hidden) return;
      const d = Math.abs(slide.offsetLeft - base - this.track.scrollLeft);
      if (d < best) { best = d; index = i; }
    });
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
    this.revealThumb(this.thumbs[index]);
    this.updateCounter(index);
  }
  disconnectedCallback() {
    this._lifecycle?.abort();
    clearTimeout(this._t);
    clearTimeout(this._rt);
    this._railObserver?.disconnect();
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Prix effectif de l'abonnement sélectionné ----------
   Reflète l'allocation du selling plan coché : prix principal (avec l'ancien
   prix barré si le plan est remisé) et prix unitaire effectif pour les totaux.
   Sans plan actif, restaure le prix standard mémorisé. */
Converto.syncSavings = function (root, price, compare) {
  const saving = root.querySelector('[data-savings]');
  if (saving) {
    const diff = compare - price;
    saving.hidden = !(diff > 0 && saving.dataset.savingsTemplate);
    if (!saving.hidden) saving.textContent = saving.dataset.savingsTemplate.replace('{amount}', Converto.formatMoney(diff));
  }
  const badge = root.querySelector('[data-sale-badge]');
  if (badge) badge.hidden = !(compare > price);
};

Converto.applyPlanPricing = function (root) {
  const priceEl = root.querySelector('[data-price]');
  if (priceEl && !priceEl.dataset.standardHtml) priceEl.dataset.standardHtml = priceEl.innerHTML;
  const unit = Number(root.dataset.unitPrice) || 0;
  const checked = root.querySelector('input[name="selling_plan"]:checked');
  const planId = checked?.value || root.querySelector('input[type="hidden"][name="selling_plan"]')?.value;
  const variantId = root.querySelector('form [name="id"]')?.value;
  let variant;
  try { variant = JSON.parse(root.querySelector('[data-product-json]')?.textContent || '[]').find((v) => String(v.id) === variantId); } catch (e) { /* repli sur le rendu initial */ }
  const allocation = planId ? variant?.allocations?.find((a) => String(a.plan) === planId) : null;
  const alloc = allocation ? Number(allocation.price) : planId ? parseInt(checked?.dataset.allocPrice, 10) : NaN;
  let effective = unit;
  let compare = Number(priceEl?.dataset.comparePrice) || 0;
  if (!isNaN(alloc)) {
    effective = alloc;
    compare = Math.max(compare, alloc < unit ? unit : 0);
    Converto.setPrice(priceEl, { price: alloc, compare_at_price: compare, unit_price: allocation?.unit_price, unit_ref: variant?.unit_ref || priceEl?.dataset.unitRef });
  } else if (priceEl?.dataset.standardHtml) {
    priceEl.innerHTML = priceEl.dataset.standardHtml;
  }
  Converto.syncSavings(root, effective, compare);
  root.dataset.effectiveUnitPrice = effective;
};

/* ---------- Total courant : prix du bouton d'achat + paiement fractionné ---------- */
Converto.updateTotals = function (scope, { total = null } = {}) {
  let t = total;
  if (t === null) {
    const btnEl = scope.querySelector('[data-btn-price]');
    let unit = parseInt(scope.dataset.effectiveUnitPrice || scope.dataset.unitPrice || (btnEl && btnEl.dataset.unitPrice), 10) || 0;
    const qtyInput = scope.querySelector('form input[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    const id = scope.querySelector('form [name="id"]')?.value;
    const state = scope.querySelector('[data-product-json]');
    let nativeBreaks = [];
    try { nativeBreaks = JSON.parse(state?.textContent || '[]').find((variant) => String(variant.id) === id)?.quantity?.breaks || []; } catch (e) { /* aucun tarif natif */ }
    const hasPlan = !!scope.querySelector('[name="selling_plan"]:checked')?.value || !!scope.querySelector('input[type="hidden"][name="selling_plan"]')?.value;
    if (!hasPlan && nativeBreaks.length) {
      const totalQuantity = qty + Number(qtyInput?.dataset.variantCartQuantity || 0);
      nativeBreaks.forEach((priceBreak) => { if (totalQuantity >= priceBreak.minimum_quantity) unit = priceBreak.price; });
      const priceEl = scope.querySelector('[data-price]');
      const compare = Math.max(Number(priceEl?.dataset.comparePrice) || 0, unit < Number(scope.dataset.unitPrice) ? Number(scope.dataset.unitPrice) : 0);
      Converto.setPrice(priceEl, { price: unit, compare_at_price: compare,
        unit_price: unit === Number(scope.dataset.unitPrice) ? Number(priceEl?.dataset.unitPrice) : null, unit_ref: priceEl?.dataset.unitRef });
      Converto.syncSavings(scope, unit, compare);
    }
    // Seule une allocation native d’abonnement peut remplacer le total catalogue.
    const selectedBreak = scope.querySelector('input[name="qb-offer"]:checked');
    if (!nativeBreaks.length && selectedBreak?.dataset.sellingPlan && !selectedBreak.disabled && parseInt(selectedBreak.value, 10) === qty && selectedBreak.dataset.total) {
      t = parseInt(selectedBreak.dataset.total, 10);
    } else {
      t = unit * qty;
    }
  }
  const el = scope.querySelector('[data-btn-price]');
  if (el) el.textContent = Converto.formatMoney(t);
  const inst = scope.querySelector('[data-installments]');
  if (inst) {
    const count = parseInt(inst.dataset.installments, 10) || 3;
    const amountEl = inst.querySelector('[data-installments-amount]');
    if (amountEl) amountEl.textContent = `${count} x ${Converto.formatMoney(Math.round(t / count))}`;
  }
  const stickyPrice = scope.querySelector('sticky-atc [data-sticky-price]')
    || (scope.closest && scope.closest('.shopify-section') || document).querySelector('sticky-atc [data-sticky-price]');
  if (stickyPrice) stickyPrice.textContent = Converto.formatMoney(t);
  // La jauge de livraison offerte se projette sur ce même total courant
  if (scope.dataset) scope.dataset.projectedTotal = t;
  if (Converto.refreshShippingBars) Converto.refreshShippingBars({ refetch: false });
};
/* Alias conservé pour compatibilité */
Converto.updateBtnPrice = function (scope, opts) {
  Converto.updateTotals(scope, opts || {});
};

/* ---------- Cross-sell : sélection multiple et ajout groupé ---------- */
class GoalMultiselect extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.totalEl = this.querySelector('[data-goal-total]');
    this.btn = this.querySelector('[data-goal-add]');
    this.currentEl = this.querySelector('[data-goal-current]');
    this.addEventListener('change', () => this.update(), { signal: this._lifecycle.signal });
    // Pilules de variantes : cliquer choisit la variante ET sélectionne la
    // carte ; re-cliquer la pilule active retire la carte de la sélection.
    this.addEventListener('pointerdown', (e) => {
      const pill = e.target.closest('.goal-variant-pill');
      if (!pill) return;
      const input = pill.querySelector('input');
      const check = pill.closest('.goal-card').querySelector('[data-goal-check]');
      this._pillWasActive = input.checked && check.checked;
    }, { signal: this._lifecycle.signal });
    this.addEventListener('click', (e) => {
      if (e.target.tagName !== 'INPUT' || !e.target.matches('[data-goal-variant-radio]')) return;
      const check = e.target.closest('.goal-card').querySelector('[data-goal-check]');
      check.checked = !this._pillWasActive;
      if (!check.checked) e.target.checked = false;
      this.update();
    }, { signal: this._lifecycle.signal });
    if (this.btn) this.btn.addEventListener('click', () => this.addSelection(), { signal: this._lifecycle.signal });
    if (this.currentEl) {
      // La ligne « inclus » suit la variante sélectionnée dans le bloc d'achat
      document.addEventListener('variant:changed', (e) => {
        const v = e.detail && e.detail.variant;
        if (!v) return;
        this.currentEl.dataset.price = v.price;
        this.currentEl.dataset.variantId = v.id;
        this.currentEl.dataset.nativeMin = v.quantity?.rule?.min || 1;
        this.currentEl.dataset.nativeStep = v.quantity?.rule?.increment || 1;
        this.currentEl.dataset.nativeMax = v.quantity?.rule?.max ?? '';
        const priceEl = this.currentEl.querySelector('[data-goal-current-price]');
        if (priceEl) priceEl.textContent = Converto.formatMoney(v.price);
        const titleEl = this.currentEl.querySelector('[data-goal-current-variant]');
        if (titleEl) titleEl.textContent = v.title;
        this.update();
      }, { signal: this._lifecycle.signal });
    }
    this.update();
  }
  currentVariantId() {
    if (this.currentEl && this.currentEl.dataset.variantId) return parseInt(this.currentEl.dataset.variantId, 10);
    const scope = document.querySelector('[data-product-scope]');
    const idInput = scope && scope.querySelector('form[action*="/cart/add"] [name="id"]');
    return idInput ? parseInt(idInput.value, 10) : null;
  }
  checks() {
    return Array.from(this.querySelectorAll('[data-goal-check]'));
  }
  itemFor(check) {
    const card = check.closest('.goal-card');
    const radio = card && card.querySelector('[data-goal-variant-radio]:checked');
    if (radio) {
      return { id: parseInt(radio.value, 10), price: parseInt(radio.dataset.price, 10) || 0 };
    }
    const sel = card && card.querySelector('[data-goal-variant]');
    if (sel && sel.selectedOptions.length) {
      const opt = sel.selectedOptions[0];
      return { id: parseInt(opt.value, 10), price: parseInt(opt.dataset.price, 10) || 0 };
    }
    return { id: parseInt(check.value, 10), price: parseInt(check.dataset.price, 10) || 0 };
  }
  update() {
    const routine = !!this.currentEl;
    let total = routine ? parseInt(this.currentEl.dataset.price, 10) || 0 : 0;
    let count = 0;
    this.checks().forEach((check) => {
      const item = this.itemFor(check);
      const card = check.closest('.goal-card');
      const priceEl = card && card.querySelector('[data-goal-price]');
      if (priceEl) priceEl.textContent = (routine ? '+ ' : '') + Converto.formatMoney(item.price);
      if (check.checked) {
        total += item.price;
        count += 1;
      }
    });
    if (this.totalEl) this.totalEl.textContent = Converto.formatMoney(total);
    const constrained = routine && (Number(this.currentEl.dataset.nativeMin) > 1 || Number(this.currentEl.dataset.nativeStep) > 1 || this.currentEl.dataset.nativeMax !== '');
    if (this.btn) this.btn.toggleAttribute('disabled', constrained || (count === 0 && !routine));
    const help = this.querySelector('[data-goal-quantity-help]');
    if (help) help.hidden = !constrained;
  }
  async addSelection() {
    const items = this.checks()
      .filter((check) => check.checked)
      .map((check) => ({ id: this.itemFor(check).id, quantity: 1 }));
    if (this.currentEl) {
      const currentId = this.currentVariantId();
      if (currentId) items.unshift({ id: currentId, quantity: 1 });
    }
    if (!items.length || this.btn.disabled) return;
    Converto.lockBtn(this.btn);
    try {
      await Converto.cart.add(items);
      this.checks().forEach((check) => { check.checked = false; });
    } catch (e) { /* toast d'erreur déjà géré par Converto.cart.add */ }
    Converto.unlockBtn(this.btn);
    this.update();
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
}
customElements.define('goal-multiselect', GoalMultiselect);

/* ---------- Cartes d'offres : une carte = une vraie variante ---------- */
document.addEventListener('change', (e) => {
  if (!e.target.matches('input[name="offer-variant"]')) return;
  const scope = e.target.closest('[data-product-scope]');
  if (!scope) return;
  const script = scope.querySelector('[data-product-json]') || document.querySelector('[data-product-json]');
  if (!script) return;
  const variants = JSON.parse(script.textContent);
  const variant = variants.find((v) => String(v.id) === e.target.value);
  if (!variant) return;
  document.dispatchEvent(new CustomEvent('variant:changed', {
    detail: { variant, productId: script.dataset.productJson }
  }));
  const url = new URL(window.location.href);
  url.searchParams.set('variant', variant.id);
  window.history.replaceState({}, '', url.toString());
});

document.addEventListener('change', (e) => {
  if (!e.target.matches('input[name="quantity"]')) return;
  const scope = e.target.closest('[data-product-scope]');
  if (scope) Converto.updateBtnPrice(scope);
});

/* ---------- Paliers de quantité ---------- */
class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.addEventListener('change', (e) => {
      const input = e.target.closest('input[name="qb-offer"]');
      if (!input || input.disabled) return;
      this.querySelectorAll('.quantity-break').forEach((el) => el.classList.remove('is-selected'));
      input.closest('.quantity-break')?.classList.add('is-selected');
      const qty = parseInt(input.value, 10) || 1;
      const scope = this.closest('[data-product-scope]') || document;
      const qtyInput = scope.querySelector('form input[name="quantity"]');
      if (qtyInput) {
        qtyInput.value = qty;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // Offre abonnement intégrée : synchronise le selling plan du formulaire
      const form = scope.querySelector('product-form form');
      if (form) {
        const planId = input.dataset.sellingPlan || '';
        const planRadios = form.querySelectorAll('input[type="radio"][name="selling_plan"]');
        if (planRadios.length) {
          let matched = false;
          planRadios.forEach((radio) => {
            const isMatch = radio.value === planId;
            radio.checked = isMatch ? true : (planId === '' && radio.value === '' ? true : false);
            if (isMatch) matched = true;
          });
          if (planId && !matched) planRadios.forEach((r) => { r.checked = r.value === ''; });
          form.querySelectorAll('.selling-plan').forEach((el) => {
            el.classList.toggle('is-selected', el.querySelector('input')?.checked || false);
          });
        } else {
          let hidden = form.querySelector('input[type="hidden"][name="selling_plan"]');
          if (planId) {
            if (!hidden) {
              hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.name = 'selling_plan';
              form.appendChild(hidden);
            }
            hidden.value = planId;
          } else if (hidden) {
            hidden.remove();
          }
        }
      }
      Converto.updateTotals(scope);
    }, { signal: this._lifecycle.signal });
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
}
customElements.define('quantity-breaks', QuantityBreaks);

/* ---------- Bundle : ajout groupé ---------- */
class BundleForm extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.btn = this.querySelector('[data-bundle-add]');
    this.syncRules();
    this.btn?.addEventListener('click', async (e) => {
      e.preventDefault();
      const items = Array.from(this.querySelectorAll('[data-bundle-variant]'))
        .map((el) => ({ id: parseInt(el.dataset.bundleVariant, 10), quantity: 1 }));
      if (!items.length || this.btn.disabled) return;
      this.btn.classList.add('is-loading');
      try { await Converto.cart.add(items); } catch (err) { /* géré */ }
      this.btn.classList.remove('is-loading');
    }, { signal: this._lifecycle.signal });
  }
  syncRules() {
    const constrained = Array.from(this.querySelectorAll('[data-bundle-variant]')).some((item) => Number(item.dataset.nativeMin) > 1 || Number(item.dataset.nativeStep) > 1 || item.dataset.nativeMax !== '');
    this.btn?.toggleAttribute('disabled', constrained);
    const help = this.querySelector('[data-bundle-quantity-help]');
    if (help) help.hidden = !constrained;
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
}
customElements.define('bundle-form', BundleForm);

/* ---------- Recommandations produits (API Shopify) ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.loaded || this.observer || this.controller) return;
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) { this.load(); return; }
    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.observer.disconnect();
      this.observer = null;
      this.load();
    }, { rootMargin: '200px 0px' });
    // The compact block stays hidden until it has useful recommendations.
    this.observer.observe(this.hasAttribute('data-compact') ? this.parentElement : this);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.controller?.abort();
    this.controller = null;
  }
  async load() {
    if (this.controller || this.loaded || !this.dataset.url) return;
    const controller = new AbortController();
    this.controller = controller;
    try {
      const res = await fetch(this.dataset.url, { signal: controller.signal });
      if (!res.ok) throw new Error('Recommendations unavailable');
      const html = await res.text();
      if (controller.signal.aborted || !this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (this.hasAttribute('data-compact')) {
        const fresh = doc.querySelector('[data-product-cards]');
        const hasCards = !!fresh?.querySelector('.product-card');
        this.querySelector('[data-complementary-cards]').innerHTML = hasCards ? fresh.innerHTML : '';
        this.querySelector('[data-complementary-content]').hidden = !hasCards;
        const hint = this.querySelector('[data-complementary-empty]');
        if (hint) hint.hidden = hasCards;
        this.hidden = !hasCards && !window.Shopify?.designMode;
      } else {
        const fresh = doc.querySelector('product-recommendations');
        if (fresh?.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      }
      this.loaded = true;
    } catch (e) { /* Keep the editor hint; empty storefront blocks remain hidden. */ }
    finally { if (this.controller === controller) this.controller = null; }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- Zoom galerie (lightbox) ---------- */
/* Les zones zoomables deviennent de vrais boutons (clavier + lecteur d'écran) */

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const zoomEl = e.target.closest('[data-zoomable]');
  if (zoomEl) { e.preventDefault(); zoomEl.click(); }
});

document.addEventListener('click', (e) => {
  const zoomEl = e.target.closest('[data-zoomable]');
  if (!zoomEl) return;
  const srcImg = zoomEl.querySelector('img');
  const src = zoomEl.dataset.zoomSrc || srcImg?.src || zoomEl.src;
  if (!src) return;
  const s = window.themeSettings.strings;
  const overlay = document.createElement('div');
  overlay.className = 'lightbox';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', s.zoomImage || '');
  overlay.innerHTML = `<button type="button" class="lightbox__close" aria-label="${s.close || ''}">&times;</button><img src="${src}" alt="${(srcImg?.alt || '').replace(/"/g, '&quot;')}">`;
  const dismiss = () => {
    overlay.remove();
    document.removeEventListener('keydown', esc);
    if (document.contains(zoomEl)) zoomEl.focus();
  };
  function esc(ev) { if (ev.key === 'Escape') dismiss(); }
  overlay.addEventListener('click', dismiss);
  document.addEventListener('keydown', esc);
  document.body.appendChild(overlay);
  Converto.trapFocus(overlay);
});

/* ---------- Sticky add-to-cart ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.scope = this.closest('.shopify-section') || document;
    this.target = this.scope.querySelector('[data-main-atc]');
    this.select = this.querySelector('select[name="id"]');
    this.btn = this.querySelector('[data-sticky-add]');
    if (!this.target) return;
    // Mode « dès le chargement » : la barre reste posée, l'observateur ne la
    // pilote plus. Sans cela elle resterait masquée tant que le bouton
    // principal est à l'écran, donc invisible à l'arrivée sur la fiche.
    if (this.classList.contains('sticky-atc--always')) {
      this.classList.add('is-visible');
    } else if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(this.target);
    }

    this.btn?.addEventListener('click', async (e) => {
      e.preventDefault();
      if (this.btn.disabled) return;
      // Soumet le formulaire produit principal : quantité, propriétés, selling plan
      // et upsells cochés sont préservés, avec la validation native des champs requis.
      // Le select du sticky est déjà synchronisé au formulaire via variant:changed.
      // Résolution limitée à la section produit : les product-form des cartes
      // d'autres sections (quick add, cross-sell) ne doivent jamais être pris.
      const section = this.closest('.shopify-section');
      const mainForm = this.target.closest('form')
        || (section && section.querySelector('form[action*="/cart/add"]'))
        || document.querySelector('.product-page form[action*="/cart/add"]');
      if (mainForm) {
        if (typeof mainForm.requestSubmit === 'function') mainForm.requestSubmit();
        else mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        return;
      }
      // Repli sans formulaire (ex. section isolée) : ajout direct de la variante
      const id = this.select ? parseInt(this.select.value, 10) : parseInt(this.dataset.variantId, 10);
      Converto.lockBtn(this.btn);
      try { await Converto.cart.add({ id, quantity: 1 }); } catch (err) { /* géré */ }
      Converto.unlockBtn(this.btn);
    }, { signal: this._lifecycle.signal });

    // La synchronisation depuis le sélecteur principal est assurée par
    // Converto.updateProductState. Ici : le sens inverse, le select du sticky
    // pilote aussi le moteur d'état (et donc toute la fiche).
    this.select?.addEventListener('change', () => {
      const jsonScript = this.scope.querySelector('[data-product-json]');
      if (!jsonScript) return;
      const variants = JSON.parse(jsonScript.textContent);
      const variant = variants.find((v) => v.id === parseInt(this.select.value, 10));
      if (variant) {
        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variant, productId: jsonScript.dataset.productJson }
        }));
        // Même comportement que le sélecteur principal : l'URL suit la variante
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }, { signal: this._lifecycle.signal });
  }
  disconnectedCallback() { this._lifecycle?.abort(); this.observer?.disconnect(); }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------- Compte à rebours d'expédition (bloc "Expédition le jour même") ---------- */
class ShippingCutoff extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector('[data-cutoff-text]');
    if (!this.textEl) return;
    this.update();
    this.timer = setInterval(() => this.update(), 60000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
  update() {
    const cutoff = parseInt(this.dataset.cutoffHour, 10) || 15;
    const days = (this.dataset.days || '')
      .split(',')
      .filter((s) => s !== '')
      .map((s) => parseInt(s, 10));
    // Heure et jour dans le fuseau de la boutique (pas celui du visiteur) :
    // la promesse d'expédition reste la même partout dans le monde
    let day;
    let nowMinutes;
    try {
      const tz = this.dataset.timezone || 'Europe/Paris';
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());
      const get = (type) => (parts.find((p) => p.type === type) || {}).value;
      day = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[get('weekday')];
      nowMinutes = (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10);
    } catch (e) {
      const now = new Date();
      day = now.getDay();
      nowMinutes = now.getHours() * 60 + now.getMinutes();
    }
    let text = '';
    if (days.includes(day) && nowMinutes < cutoff * 60) {
      const minutes = cutoff * 60 - nowMinutes;
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      const timeStr = h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
      text = (this.dataset.textOpen || '').replace('{time}', timeStr);
      this.classList.add('is-open');
    } else {
      text = this.dataset.textClosed || '';
      this.classList.remove('is-open');
    }
    this.textEl.textContent = text;
    this.hidden = text === '';
  }
}
customElements.define('shipping-cutoff', ShippingCutoff);

/* ---------- Produits récemment consultés : enregistrement du handle ---------- */
(function () {
  const record = () => {
    const scope = document.querySelector('[data-product-scope][data-product-handle]');
    const handle = scope && scope.dataset.productHandle;
    if (!handle) return;
    try {
      const key = 'converto-recently-viewed';
      let list = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(list)) list = [];
      list = [handle].concat(list.filter((h) => h && h !== handle)).slice(0, 12);
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) { /* stockage indisponible */ }
  };
  // F07 : une fiche prérendue mais jamais affichée ne pollue pas l'historique
  if (document.prerendering) document.addEventListener('prerenderingchange', record, { once: true });
  else record();
})();

/* ---------- Retour en stock (Klaviyo) ---------- */
class BackInStock extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.form = this.querySelector('form');
    this.message = this.querySelector('[data-bis-message]');
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.subscribe();
    }, { signal: this._lifecycle.signal });
    // Suit la variante sélectionnée (le bloc reste dans le DOM tant que la page n'est pas rechargée)
    document.addEventListener('variant:changed', (e) => {
      const scope = this.closest('[data-product-scope]');
      if (scope && String(e.detail.productId) !== scope.dataset.productScope) return;
      const variant = e.detail.variant;
      if (!variant) return;
      this.dataset.variantId = variant.id;
      this.hidden = variant.available;
    }, { signal: this._lifecycle.signal });
  }
  async subscribe() {
    if (!this.dataset.companyId) return;
    const emailInput = this.form?.querySelector('input[name="email"]');
    const email = emailInput?.value.trim();
    if (!email) return;
    const btn = this.form.querySelector('[type="submit"]');
    btn?.classList.add('is-loading');
    try {
      const body = new URLSearchParams({
        a: this.dataset.companyId,
        email,
        variant: this.dataset.variantId || '',
        platform: 'shopify'
      });
      const res = await fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (!res.ok) throw new Error('back-in-stock');
      if (!this.isConnected) return;
      this.showMessage(this.dataset.success, true);
      this.form.reset();
    } catch (err) {
      this.showMessage(this.dataset.error, false);
    }
    btn?.classList.remove('is-loading');
  }
  showMessage(text, success) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.classList.toggle('form-message--success', success);
    this.message.classList.toggle('form-message--error', !success);
    this.message.hidden = text ? false : true;
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
}
customElements.define('back-in-stock', BackInStock);

/* ---------- Achat rapide avec options (cartes et produit vedette) ----------
   Petit panneau de choix de variantes dans un <dialog> natif (couche
   supérieure : insensible aux transforms des cartes animées). L'ajout
   passe par le formulaire central product-form. */
class QuickBuy extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.dialog = this.querySelector('dialog');
    this.openBtn = this.querySelector('[data-qb-open]');
    this.selects = Array.from(this.querySelectorAll('[data-qb-option]'));
    this.idInput = this.querySelector('form input[name="id"]');
    this.priceEl = this.querySelector('[data-qb-price]');
    this.submitBtn = this.querySelector('form [type="submit"]');
    try {
      this.variants = JSON.parse(this.querySelector('[data-qb-variants]').textContent);
    } catch (e) { this.variants = []; }
    if (!this.dialog || !this.openBtn || !this.variants.length) return;
    this.openBtn.hidden = false;
    const fallback = this.querySelector('[data-qb-fallback]');
    if (fallback) fallback.hidden = true;
    this.openBtn.addEventListener('click', () => {
      this.dialog.showModal();
      this.update();
    }, { signal: this._lifecycle.signal });
    this.querySelector('[data-qb-close]')?.addEventListener('click', () => this.dialog.close(), { signal: this._lifecycle.signal });
    // Clic sur le backdrop : la cible est le dialog lui-même
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); }, { signal: this._lifecycle.signal });
    this.dialog.addEventListener('close', () => this.openBtn.focus(), { signal: this._lifecycle.signal });
    this.selects.forEach((s) => s.addEventListener('change', () => this.update(), { signal: this._lifecycle.signal }));
    this.addEventListener('change', (event) => { if (event.target.matches('[name="quantity"]')) this.update(); }, { signal: this._lifecycle.signal });
    // Ajout confirmé (un seul panneau ouvert à la fois) : on referme
    document.addEventListener('cart:added', () => { if (this.dialog.open) this.dialog.close(); }, { signal: this._lifecycle.signal });
    this.update();
  }
  update() {
    const chosen = this.selects.map((s) => s.value);
    const variant = this.variants.find((v) => chosen.every((val, i) => v.options[i] === val));
    const productLink = this.querySelector('[data-qb-view-product]');
    if (productLink) {
      productLink.href = variant?.url || this.dataset.productUrl;
      if (variant) {
        productLink.dataset.itemId = variant.sku || variant.id;
        productLink.dataset.itemVariant = variant.title || '';
        productLink.dataset.itemPrice = variant.price / 100;
      } else {
        delete productLink.dataset.itemId;
        delete productLink.dataset.itemVariant;
        delete productLink.dataset.itemPrice;
      }
    }
    const label = this.submitBtn?.querySelector('.btn__label');
    if (variant) {
      this.idInput.value = variant.id;
      Converto.quantity.sync(this, variant);
    }
    if (this.priceEl && variant) {
      const input = this.querySelector('[name="quantity"]');
      const quantity = Number(input?.value || 1) + Number(input?.dataset.variantCartQuantity || 0);
      let price = variant.price;
      (variant.quantity?.breaks || []).forEach((tier) => { if (quantity >= tier.minimum_quantity) price = tier.price; });
      Converto.setPrice(this.priceEl.querySelector('[data-price]') || this.priceEl, { ...variant, price,
        compare_at_price: Math.max(variant.compare_at_price || 0, price < variant.price ? variant.price : 0),
        unit_price: price === variant.price ? variant.unit_price : null });
    }
    if (this.priceEl && !variant) this.priceEl.replaceChildren();
    if (this.submitBtn && label) {
      if (!variant) {
        this.submitBtn.disabled = true;
        label.textContent = this.submitBtn.dataset.textUnavailable;
      } else if (!variant.available) {
        this.submitBtn.disabled = true;
        label.textContent = this.submitBtn.dataset.textSoldout;
      } else {
        this.submitBtn.disabled = false;
        label.textContent = this.submitBtn.dataset.textAdd;
      }
    }
  }
  disconnectedCallback() { this._lifecycle?.abort(); if (this.dialog?.open) this.dialog.close(); }
}
customElements.define('quick-buy', QuickBuy);

/* P13 : initialisation des totaux sur la quantité native rendue côté serveur. */
Converto.initProductSections = function (root = document) {
  const scopes = [...(root.matches?.('[data-product-scope]') ? [root] : []), ...root.querySelectorAll('[data-product-scope]')];
  scopes.forEach(scope => {
    Converto.applyPlanPricing(scope);
    Converto.quantity.syncTiers(scope);
    Converto.updateTotals(scope);
  });
root.querySelectorAll('[data-zoomable]').forEach((el) => {
  el.tabIndex = 0;
  el.setAttribute('role', 'button');
  if (!el.getAttribute('aria-label')) {
    el.setAttribute('aria-label', window.themeSettings.strings.zoomImage || '');
  }
});
};
Converto.initProductSections();
document.addEventListener('shopify:section:load', event => Converto.initProductSections(event.target));
